/**
 * Notification UI — **même recette pixel-for-pixel** que ArchivesFacturation (toast « Succès » / erreur / etc.).
 * Utilisable depuis **tout** le monorepo (facturation, inventaire, RH, …).
 *
 * Référence visuelle : `ArchivesFacturation.js` — blocs `ARCHIVES_TOAST_SAVE_MATCH_SHELL`, `archivesToastShell`, carte `motion.div`.
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  CheckCircle as CheckCircleIcon,
  AlertTriangle as ExclamationTriangleIcon,
  Trash2 as TrashIcon,
  Clock as ClockIcon,
} from 'lucide-react';
import i18n from '../../i18n';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';

// --- Qawaem ERP notification shells (forest / copper / red) ---

/** Qawaem ERP — forest green success shell (replaces legacy blue). */
const QAWAEM_SUCCESS_SHELL = {
  wrapper:
    'bg-gradient-to-br from-[#f4faf4] via-[#eef6ee] to-[#faf8f4] border-[#6e9b6e]/55',
  shadow: '0 12px 40px rgba(46,69,46,0.18), 0 0 0 1px rgba(110,155,110,0.12)',
  iconBox: 'bg-[#6e9b6e] border-[#5a855a] text-white',
  textClass: 'text-[#2e452e]',
  topLabelClass: 'text-[#5a855a]',
  Icon: CheckCircleIcon,
};

/** Qawaem ERP — copper/bronze for create actions. */
const QAWAEM_CREATED_SHELL = {
  wrapper:
    'bg-gradient-to-br from-[#faf8f4] via-[#f7f0e8] to-[#faf8f4] border-[#a67c3d]/50',
  shadow: '0 12px 40px rgba(112,72,32,0.16), 0 0 0 1px rgba(166,124,61,0.14)',
  iconBox: 'bg-[#a67c3d] border-[#8f6634] text-white',
  textClass: 'text-[#2e452e]',
  topLabelClass: 'text-[#704820]',
  Icon: CheckCircleIcon,
};

/** Coquille pixel-aligned avec le toast après sauvegarde document (carte Succès principale). */
export const APP_NOTIFICATION_SAVE_MATCH_SHELL = {
  ...QAWAEM_SUCCESS_SHELL,
};

/**
 * Résout la coquille comme `archivesToastShell` (statuts Archives + variante explicite).
 *
 * @param {object} opts
 * @param {string} [opts.status] — ex. 'error' | 'deleted' | 'pending' | 'success' | 'completed' | 'paid'
 * @param {'success'|'error'|'deleted'|'progress'|'default'|'cta'} [opts.variant]
 * @param {boolean} [opts.showActions] — même rôle que `toast.showActions` Archives (shell succès / CTA)
 */
export function resolveAppNotificationShell({ status, variant: variantIn, showActions }) {
  const variant = variantIn || mapArchiveStatusToVariant(status);
  const qawaem = isSaudiBrand();

  if (variant === 'error' || status === 'error') {
    return {
      variant: 'error',
      wrapper: qawaem
        ? 'bg-gradient-to-br from-[#fff5f5] via-[#fef2f2] to-[#faf8f4] border-red-400/55'
        : 'bg-gradient-to-br from-slate-900/98 via-slate-950/95 to-red-950/30 border-red-400/55',
      shadow: qawaem
        ? '0 12px 40px rgba(185,28,28,0.16), 0 0 0 1px rgba(248,113,113,0.12)'
        : '0 0 42px rgba(248,113,113,0.38), 0 0 80px rgba(239,68,68,0.16)',
      iconBox: qawaem
        ? 'bg-red-600 border-red-700 text-white'
        : 'bg-red-500/20 border-red-400/45 text-red-100',
      iconBoxStyle: undefined,
      textClass: qawaem ? 'text-[#2e452e]' : 'text-white',
      topLabelClass: qawaem ? 'text-red-700' : 'text-red-200/80',
      Icon: ExclamationTriangleIcon,
    };
  }

  if (showActions || variant === 'cta') {
    return {
      variant: 'cta',
      ...QAWAEM_SUCCESS_SHELL,
      iconBoxStyle: undefined,
    };
  }

  if (variant === 'created') {
    return {
      variant: 'created',
      ...QAWAEM_CREATED_SHELL,
      iconBoxStyle: undefined,
    };
  }

  if (variant === 'deleted' || status === 'deleted') {
    return {
      variant: 'deleted',
      wrapper: qawaem
        ? 'bg-gradient-to-br from-[#fff5f5] via-[#fef2f2] to-[#faf8f4] border-red-400/50'
        : 'bg-gradient-to-br from-slate-900/98 via-slate-950/95 to-rose-950/25 border-rose-400/45',
      shadow: qawaem
        ? '0 12px 40px rgba(185,28,28,0.14), 0 0 0 1px rgba(248,113,113,0.1)'
        : '0 0 40px rgba(251,113,133,0.28), 0 0 72px rgba(244,63,94,0.12)',
      iconBox: qawaem
        ? 'bg-red-600 border-red-700 text-white'
        : 'bg-rose-500/15 border-rose-400/35 text-rose-50',
      iconBoxStyle: undefined,
      textClass: qawaem ? 'text-[#2e452e]' : 'text-white',
      topLabelClass: qawaem ? 'text-red-700' : 'text-rose-200/80',
      Icon: TrashIcon,
    };
  }

  if (variant === 'progress' || status === 'pending') {
    return {
      variant: 'progress',
      wrapper: qawaem
        ? 'bg-gradient-to-br from-[#faf8f4] via-[#f7f0e8] to-[#faf8f4] border-[#a67c3d]/45'
        : 'bg-gradient-to-br from-slate-800/98 via-amber-950/25 to-slate-900/98 border-amber-400/38',
      shadow: qawaem
        ? '0 12px 36px rgba(112,72,32,0.14)'
        : '0 0 38px rgba(245,158,11,0.35), 0 0 72px rgba(245,158,11,0.14)',
      iconBox: qawaem
        ? 'bg-[#a67c3d] border-[#8f6634] text-white'
        : 'bg-amber-500/18 border-amber-400/40 text-amber-50',
      iconBoxStyle: undefined,
      textClass: qawaem ? 'text-[#2e452e]' : 'text-white',
      topLabelClass: qawaem ? 'text-[#704820]' : 'text-amber-200/80',
      Icon: ClockIcon,
    };
  }

  if (
    variant === 'success' ||
    status === 'completed' ||
    status === 'success' ||
    status === 'paid'
  ) {
    return {
      variant: 'success',
      ...QAWAEM_SUCCESS_SHELL,
      iconBoxStyle: undefined,
    };
  }

  return {
    variant: 'default',
    ...QAWAEM_SUCCESS_SHELL,
    iconBoxStyle: undefined,
  };
}

function mapArchiveStatusToVariant(status) {
  if (!status) return undefined;
  const s = String(status);
  if (s === 'error') return 'error';
  if (s === 'deleted') return 'deleted';
  if (s === 'pending') return 'progress';
  if (s === 'success' || s === 'completed' || s === 'paid') return 'success';
  return undefined;
}

/** Libellé titre du bloc (11px uppercase) — même logique que Archives. */
export function appNotificationTopLabel(shellVariant) {
  if (shellVariant === 'error') return i18n.t('common.error');
  if (shellVariant === 'progress') return i18n.t('common.patience');
  if (shellVariant === 'cta' || shellVariant === 'success' || shellVariant === 'created') return i18n.t('common.success');
  if (shellVariant === 'deleted') return i18n.t('common.archives');
  return i18n.t('common.notification');
}

function topLabelClassForShell(shell) {
  if (shell.topLabelClass) return shell.topLabelClass;
  const shellVariant = shell.variant;
  if (shellVariant === 'error') return 'text-red-200/80';
  if (shellVariant === 'deleted') return 'text-rose-200/80';
  if (shellVariant === 'progress') return 'text-amber-200/80';
  if (shellVariant === 'cta' || shellVariant === 'success' || shellVariant === 'created') return 'text-emerald-200/92';
  return 'text-white/50';
}

const TOAST_SPRING = {
  type: 'spring',
  stiffness: 540,
  damping: 20,
  mass: 0.58,
};

const TOAST_MOTION = {
  initial: { opacity: 0, scale: 0.86, y: -28 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.93, y: -20 },
  transition: TOAST_SPRING,
};

const TOAST_MOTION_CENTER = {
  initial: { opacity: 0, scale: 0.94 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96 },
  transition: TOAST_SPRING,
};

const TOAST_MOTION_BOTTOM = {
  initial: { opacity: 0, scale: 0.92, y: 32 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 24 },
  transition: TOAST_SPRING,
};

/**
 * Classes boutons zone basse — **copie littérale** ArchivesFacturation (`border-white/25`, `bg-white/10`, `backdrop-blur-sm`, hover `/20`).
 * À réutiliser pour tout bouton sous le message du toast.
 */
export const NOTIFICATION_TOAST_ACTION_BUTTON_CLASS =
  'flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition-all hover:bg-white/20';

/**
 * Rangée d’actions + styles automatiques sur les `<button>` enfants (même rendu Archives si vous passez des boutons bruts).
 */
export const NOTIFICATION_TOAST_ACTIONS_FOOTER_CLASS = [
  'mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4',
  '[&_button]:flex [&_button]:items-center [&_button]:justify-center [&_button]:gap-2',
  '[&_button]:rounded-xl [&_button]:border [&_button]:border-white/25 [&_button]:bg-white/10',
  '[&_button]:px-4 [&_button]:py-2.5 [&_button]:text-sm [&_button]:font-medium [&_button]:text-white',
  '[&_button]:backdrop-blur-sm [&_button]:transition-all [&_button]:hover:bg-white/20',
].join(' ');

/** Bouton « verre » prêt à l’emploi pour les toasts (Archives / Fournisseurs / …). */
export function AppNotificationToastActionButton({ children, className = '', ...rest }) {
  return (
    <button
      type="button"
      className={`${NOTIFICATION_TOAST_ACTION_BUTTON_CLASS}${className ? ` ${className}` : ''}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * Carte notification — identique au toast Archives (position fixe, spring, structure flex).
 *
 * @param {object} props
 * @param {boolean} props.visible
 * @param {() => void} props.onDismiss
 * @param {string} [props.message] — texte brut (si `messageHtml` absent)
 * @param {string} [props.messageHtml] — HTML (comme Archives `toast.message`) — prioritaire si défini
 * @param {string} [props.status] — statut style Archives
 * @param {'success'|'error'|'deleted'|'progress'|'default'|'cta'} [props.variant]
 * @param {boolean} [props.showActions] — même shell que succès/CTA Archives
 * @param {React.ReactNode} [props.children] — zone sous le message (ex. boutons comme PDF / Créer un autre)
 * @param {string} [props.dismissAriaLabel]
 * @param {string} [props.topLabel] — override du libellé 11px uppercase (ex. « Mise à jour »)
 * @param {string} [props.className] — classes additionnelles sur la carte
 * @param {boolean} [props.showDismiss] — afficher le bouton fermer (défaut true)
 * @param {'top'|'bottom'|'center'} [props.placement] — position fixe (défaut top)
 * @param {boolean} [props.dismissOnBackdrop] — fermer au clic hors carte
 */
export function AppNotificationToast({
  visible,
  onDismiss,
  message = '',
  messageHtml,
  status,
  variant,
  showActions = false,
  children,
  dismissAriaLabel,
  topLabel,
  className = '',
  showDismiss = true,
  placement = 'top',
  dismissOnBackdrop = false,
}) {
  const resolvedDismissAriaLabel = dismissAriaLabel ?? i18n.t('common.close');

  const shell = useMemo(
    () => resolveAppNotificationShell({ status, variant, showActions }),
    [status, variant, showActions]
  );

  const ShellIcon = shell.Icon;
  const shellVariant = shell.variant;
  const toastMotion =
    placement === 'bottom'
      ? TOAST_MOTION_BOTTOM
      : placement === 'center'
        ? TOAST_MOTION_CENTER
        : TOAST_MOTION;
  const positionClass =
    placement === 'bottom'
      ? 'left-4 right-4 bottom-6 top-auto mx-auto w-full max-w-lg'
      : placement === 'center'
        ? 'left-4 right-4 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2'
        : 'left-4 right-4 top-24 mx-auto w-full max-w-lg';

  return (
    <AnimatePresence>
      {visible && dismissOnBackdrop ? (
        <motion.button
          key="app-notification-backdrop"
          type="button"
          aria-label={resolvedDismissAriaLabel}
          className="fixed inset-0 z-[99998] cursor-default bg-black/35"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onDismiss}
        />
      ) : null}
      {visible && (
        <motion.div
          key="app-notification-toast"
          role="status"
          aria-live="polite"
          className={`fixed z-[99999] overflow-hidden ${shell.textClass || 'text-white'} ${positionClass} rounded-2xl border-2 backdrop-blur-lg ${shell.wrapper}${className ? ` ${className}` : ''}`}
          style={{ boxShadow: shell.shadow }}
          initial={toastMotion.initial}
          animate={toastMotion.animate}
          exit={toastMotion.exit}
          transition={toastMotion.transition}
          onClick={(event) => event.stopPropagation()}
        >
          {(shellVariant === 'success' || shellVariant === 'cta' || shellVariant === 'created') && (
            <div
              className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl"
              aria-hidden
            >
              <div className={`absolute -bottom-12 -right-8 h-36 w-36 rounded-full blur-3xl ${shellVariant === 'created' ? 'bg-[#a67c3d]/14' : 'bg-[#6e9b6e]/14'}`} />
            </div>
          )}
          <div className="relative z-[1] flex items-start gap-4 p-5">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-solid text-white ${
                shell.iconBoxStyle ? '' : shell.iconBox
              }`}
              style={shell.iconBoxStyle}
            >
              <ShellIcon className="h-5 w-5 shrink-0 !text-white" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={`text-[11px] font-semibold uppercase tracking-wider ${topLabelClassForShell(shell)}`}
              >
                {topLabel ?? appNotificationTopLabel(shellVariant)}
              </p>
              {messageHtml != null && messageHtml !== '' ? (
                <div
                  className={`mt-1.5 text-[15px] font-semibold leading-snug [&_ul]:font-normal ${shell.textClass || 'text-white'}`}
                  dangerouslySetInnerHTML={{ __html: messageHtml }}
                />
              ) : (
                <p className={`mt-1.5 text-[15px] font-semibold leading-snug whitespace-pre-wrap break-words ${shell.textClass || 'text-white'}`}>
                  {message}
                </p>
              )}
              {children ? (
                <div className={NOTIFICATION_TOAST_ACTIONS_FOOTER_CLASS}>{children}</div>
              ) : null}
            </div>
            {showDismiss ? (
              <button
                type="button"
                aria-label={resolvedDismissAriaLabel}
                onClick={onDismiss}
                className={`-m-1 shrink-0 rounded-lg p-1.5 transition-colors ${
                  shell.textClass
                    ? 'bg-black/20 text-white hover:bg-black/30'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <X className="h-5 w-5 !text-white" />
              </button>
            ) : null}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const DEFAULT_AUTO_MS = 4200;

/**
 * État + timer pour afficher une notification (toute app).
 *
 * @param {number} [autoDismissMs]
 * @returns {{
 *   toast: { visible: boolean, message: string, messageHtml?: string, status?: string, variant?: string, showActions?: boolean },
 *   show: (message: string, options?: { variant?: string, status?: string, showActions?: boolean, ms?: number, messageHtml?: string }) => void,
 *   dismiss: () => void
 * }}
 */
export function useAppNotificationToast(autoDismissMs = DEFAULT_AUTO_MS) {
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    messageHtml: undefined,
    status: undefined,
    variant: 'success',
    showActions: false,
    topLabel: undefined,
  });
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setToast((p) => ({ ...p, visible: false }));
  }, []);

  const show = useCallback(
    (message, arg2, arg3) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);

      let variant = 'success';
      let ms = autoDismissMs;
      let messageHtml;
      let status;
      let showActions = false;
      let topLabel;

      if (arg2 != null && typeof arg2 === 'object' && !Array.isArray(arg2)) {
        variant = arg2.variant ?? 'success';
        ms = arg2.ms ?? autoDismissMs;
        messageHtml = arg2.messageHtml;
        status = arg2.status;
        showActions = arg2.showActions ?? false;
        topLabel = arg2.topLabel;
      } else if (typeof arg2 === 'string') {
        variant = arg2;
        if (typeof arg3 === 'number') ms = arg3;
      } else if (typeof arg2 === 'number') {
        ms = arg2;
      }

      setToast({
        visible: true,
        message: messageHtml ? '' : message,
        messageHtml,
        status,
        variant,
        showActions,
        topLabel,
      });

      if (ms > 0) {
        timerRef.current = window.setTimeout(() => {
          setToast((p) => ({ ...p, visible: false }));
          timerRef.current = null;
        }, ms);
      }
    },
    [autoDismissMs]
  );

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  return { toast, show, dismiss };
}
